import React, { useMemo } from 'react';
import { FunctionField, FunctionFieldProps } from 'react-admin';
import { parsePhoneNumber, type Country } from 'react-phone-number-input';

interface PhoneFieldProps extends Omit<FunctionFieldProps, 'render'> {
  /**
   * Default country code to use for parsing (e.g., 'BR', 'US')
   * If not provided, will try to detect from the phone number
   */
  defaultCountry?: Country;
}

/**
 * Get country flag emoji using Unicode regional indicator symbols
 */
const getCountryFlag = (country: Country | undefined): string => {
  if (!country) return '🌐';
  try {
    // Convert country code (e.g., 'BR') to flag emoji
    // Unicode regional indicator symbols: A=U+1F1E6, B=U+1F1E7, etc.
    const codePoints = country
      .toUpperCase()
      .split('')
      .map(char => 127397 + char.charCodeAt(0));
    return String.fromCodePoint(...codePoints);
  } catch {
    return '🌐';
  }
};

/**
 * Format phone number with country flag emoji
 */
const formatPhoneNumber = (
  value: string | undefined,
  defaultCountry: Country
): string => {
  if (!value || value.trim() === '') {
    return '';
  }

  try {
    // Try to parse the phone number
    const phoneNumber = parsePhoneNumber(value, defaultCountry);
    if (phoneNumber) {
      // Get country from parsed number or use default
      const country = phoneNumber.country || defaultCountry;
      const flag = getCountryFlag(country);
      
      // Return national format with flag (e.g., "🇧🇷 (21) 97013-4999")
      const nationalFormat = phoneNumber.formatNational();
      return `${flag} ${nationalFormat}`;
    }
  } catch (error) {
    // If parsing fails, return as is
  }

  // If parsing fails, return original value
  return value;
};

/**
 * PhoneField component for React Admin that formats phone numbers for display.
 * Shows country flag emoji and formats number in national format (without country code).
 * 
 * @example
 * <PhoneField source="phone" label="Phone Number" defaultCountry="BR" />
 */
export const PhoneField: React.FC<PhoneFieldProps> = ({
  source,
  defaultCountry = 'BR',
  ...rest
}) => {
  const formatValue = useMemo(() => {
    return (record: Record<string, unknown>) => {
      const value = record[source || ''] as string | undefined;
      return formatPhoneNumber(value, defaultCountry);
    };
  }, [source, defaultCountry]);

  return (
    <FunctionField
      source={source}
      render={formatValue}
      {...rest}
    />
  );
};

